import { useState, useEffect, useCallback } from "react";
import { BrandContext } from "./context";
import { brandService } from "../../services/brand.service";
import { BrandDetails } from "../../types/brand";
import { useBrandTheme } from "../../theme/useBrandTheme";
import { getCachedBrandDetails, setCachedBrandDetails } from "./cache";

interface BrandProviderProps {
  children: React.ReactNode;
}

export const BrandProvider = ({ children }: BrandProviderProps) => {
  const [brandDetails, setBrandDetailsState] = useState<BrandDetails | null>(
    () => getCachedBrandDetails()
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  // Apply the brand's colors as runtime CSS vars while the brand portal is
  // mounted. No reset on unmount — this provider remounts on every navigation
  // and resetting would strobe back to defaults; logout resets instead.
  useBrandTheme(brandDetails?.brandAppearance);

  const setBrandDetails = useCallback((details: BrandDetails | null) => {
    setCachedBrandDetails(details);
    setBrandDetailsState(details);
  }, []);

  const fetchBrandDetails = useCallback(async () => {
    if (hasFetched) return;

    setIsLoading(true);
    try {
      const apiResponse = await brandService.getBrandDetails();
      const data = apiResponse.data;
      setBrandDetails(data);
      setHasFetched(true);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [hasFetched, setBrandDetails]);

  const refetchBrandDetails = useCallback(async () => {
    setIsLoading(true);
    try {
      const apiResponse = await brandService.getBrandDetails();
      const data = apiResponse.data;
      setBrandDetails(data);
      setHasFetched(true);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [setBrandDetails]);

  useEffect(() => {
    const token = localStorage.getItem("brand_token");
    if (token && !hasFetched) {
      fetchBrandDetails();
      setHasFetched(true);
    }
  }, [fetchBrandDetails, hasFetched]);

  const value = {
    brandDetails,
    setBrandDetails,
    isLoading,
    error,
    fetchBrandDetails,
    refetchBrandDetails,
  };

  return (
    <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
  );
};
